import { useEffect, useMemo, useState, useRef } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Beaker, Plus, Search, Archive as ArchiveIcon, RotateCcw, Loader2, UploadCloud, Sparkles, Trash2 } from "lucide-react";

export default function Formulas() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [creating, setCreating] = useState(false);
  const [importing, setImporting] = useState(false);
  const [newName, setNewName] = useState("");
  const [busy, setBusy] = useState(false);

  const reload = async () => {
    try {
      const { data } = await api.get("/formulas", {
        params: { q, include_archived: showArchived },
      });
      setRows(data.formulas || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load formulas");
    }
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [showArchived]);
  useEffect(() => { const t = setTimeout(reload, 300); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [q]);

  const createFormula = async () => {
    if (!newName.trim()) return toast.error("Formula name required");
    setBusy(true);
    try {
      const { data } = await api.post("/formulas", {
        name: newName.trim(),
        ingredients: [], instructions: [], equipment: [],
        bud_days: 30, total_quantity_unit: "mL",
      });
      toast.success("Formula created — click to add ingredients");
      setCreating(false); setNewName("");
      // Open the new detail
      window.location.href = `/formulas/${data.id}`;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Create failed");
    } finally { setBusy(false); }
  };

  const archive = async (id) => {
    if (!confirm("Archive this formula? Existing batch logs stay intact.")) return;
    try { await api.delete(`/formulas/${id}`); toast.success("Archived"); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Archive failed"); }
  };
  const hardDelete = async (f) => {
    if (!confirm(
      `PERMANENTLY DELETE "${f.name}"?\n\nThis cannot be undone. The formula record will be removed from the database. Batch logs already spawned from it stay intact (they hold a snapshot).\n\nType OK in the next prompt to confirm.`
    )) return;
    const confirm2 = prompt(`Type DELETE to permanently remove "${f.name}":`);
    if (confirm2 !== "DELETE") return toast.info("Cancelled");
    try {
      await api.delete(`/formulas/${f.id}`, { params: { hard: true } });
      toast.success(`"${f.name}" permanently deleted`);
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };
  const restore = async (id) => {
    try { await api.post(`/formulas/${id}/restore`); toast.success("Restored"); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Restore failed"); }
  };

  const filtered = useMemo(() => rows, [rows]);

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="mb-6 flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-2">
            <Beaker className="w-7 h-7 text-pink-700" /> Master Formulas
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Recipe library — each formula is the template every compounding batch scales from.
          </p>
        </div>
        {isAdmin && (
          <div className="flex items-center gap-2">
            <Button data-testid="formulas-import-btn" onClick={() => setImporting(true)}
              variant="outline" className="gap-1.5 border-pink-300 text-pink-700 hover:bg-pink-50">
              <UploadCloud className="w-4 h-4" /> Import from file
            </Button>
            <Button data-testid="formulas-new-btn" onClick={() => setCreating(true)}
              className="bg-pink-700 hover:bg-pink-800 gap-1.5">
              <Plus className="w-4 h-4" /> New Formula
            </Button>
          </div>
        )}
      </div>

      <Card className="p-3 mb-4 border-slate-200 flex flex-wrap items-center gap-2">
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input data-testid="formulas-search" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search name, description, dosage form…" className="pl-9 h-9 text-sm" />
        </div>
        <button data-testid="formulas-archived-toggle"
          onClick={() => setShowArchived((s) => !s)}
          className={`text-xs px-3 py-1 rounded-full border font-medium ${
            showArchived
              ? "bg-slate-800 text-white border-slate-800"
              : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
          }`}>
          {showArchived ? "Showing archived" : "Show archived"}
        </button>
        <span className="text-xs text-slate-500 ml-auto">{filtered.length} formula{filtered.length === 1 ? "" : "s"}</span>
      </Card>

      <Card className="p-0 overflow-hidden border-slate-200">
        {filtered.length === 0 ? (
          <div className="p-12 text-center text-sm text-slate-500">
            No formulas yet. {isAdmin && "Click New Formula to add your first recipe."}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table data-testid="formulas-table">
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead>Name</TableHead>
                  <TableHead>Dosage form</TableHead>
                  <TableHead>Route</TableHead>
                  <TableHead className="text-right">Total qty</TableHead>
                  <TableHead className="text-right">Ingredients</TableHead>
                  <TableHead>BUD</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((f) => (
                  <TableRow key={f.id} data-testid={`formula-row-${f.id}`}>
                    <TableCell>
                      <Link to={`/formulas/${f.id}`}
                        data-testid={`formula-open-${f.id}`}
                        className="font-medium text-slate-800 hover:text-pink-700 hover:underline">
                        {f.name}
                      </Link>
                      {f.description && <div className="text-xs text-slate-500 mt-0.5 truncate max-w-md">{f.description}</div>}
                    </TableCell>
                    <TableCell className="text-xs text-slate-700">{f.dosage_form || <span className="text-slate-300">—</span>}</TableCell>
                    <TableCell className="text-xs text-slate-700">{f.route_of_administration || <span className="text-slate-300">—</span>}</TableCell>
                    <TableCell className="font-mono text-xs text-right text-slate-700">
                      {f.total_quantity ? `${f.total_quantity} ${f.total_quantity_unit}` : <span className="text-slate-300">—</span>}
                    </TableCell>
                    <TableCell className="font-mono text-xs text-right text-pink-800">{(f.ingredients || []).length}</TableCell>
                    <TableCell className="text-xs text-slate-700">{f.bud_days ? `${f.bud_days} days` : <span className="text-slate-300">—</span>}</TableCell>
                    <TableCell>
                      <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${
                        f.status === "archived"
                          ? "bg-slate-100 text-slate-600 border-slate-300"
                          : "bg-emerald-50 text-emerald-800 border-emerald-300"
                      }`}>{f.status || "active"}</span>
                    </TableCell>
                    <TableCell className="text-right space-x-1 whitespace-nowrap">
                      {isAdmin && f.status !== "archived" && (
                        <Button size="sm" variant="ghost" className="h-7 gap-1 text-xs text-slate-500 hover:text-rose-700"
                          data-testid={`formula-archive-${f.id}`} onClick={() => archive(f.id)}>
                          <ArchiveIcon className="w-3 h-3" /> Archive
                        </Button>
                      )}
                      {isAdmin && f.status === "archived" && (
                        <Button size="sm" variant="outline" className="h-7 gap-1 text-xs"
                          data-testid={`formula-restore-${f.id}`} onClick={() => restore(f.id)}>
                          <RotateCcw className="w-3 h-3" /> Restore
                        </Button>
                      )}
                      {isAdmin && (
                        <Button size="sm" variant="ghost"
                          className="h-7 gap-1 text-xs text-rose-500 hover:bg-rose-50 hover:text-rose-800"
                          data-testid={`formula-delete-${f.id}`} onClick={() => hardDelete(f)}
                          title="Permanently delete (irreversible)">
                          <Trash2 className="w-3 h-3" /> Delete
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>

      <Dialog open={creating} onOpenChange={(o) => !o && setCreating(false)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>New Master Formula</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <label className="text-[10px] text-slate-500 uppercase">Formula name</label>
            <Input data-testid="formulas-new-name" value={newName}
              onChange={(e) => setNewName(e.target.value)} placeholder="e.g. Semaglutide 5 mg/mL"
              className="h-9 text-sm" autoFocus />
            <p className="text-xs text-slate-500">You'll add ingredients, instructions, BUD rule, and equipment on the next screen.</p>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setCreating(false)}>Cancel</Button>
            <Button data-testid="formulas-new-save" onClick={createFormula} disabled={busy}
              className="bg-pink-700 hover:bg-pink-800 gap-1.5">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ImportFormulaDialog open={importing} onClose={() => setImporting(false)}
        onSaved={(id) => { setImporting(false); window.location.href = `/formulas/${id}`; }} />
    </div>
  );
}

function ImportFormulaDialog({ open, onClose, onSaved }) {
  const [file, setFile] = useState(null);
  const [extracting, setExtracting] = useState(false);
  const [extracted, setExtracted] = useState(null);
  const [saving, setSaving] = useState(false);
  const inputRef = useRef();

  useEffect(() => {
    if (!open) { setFile(null); setExtracted(null); }
  }, [open]);

  const runExtract = async () => {
    if (!file) return toast.error("Pick a file first");
    setExtracting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/formulas/import-extract", fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      setExtracted(data.extracted || {});
      toast.success(`AI extracted: ${data.extracted?.name || "recipe draft"}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Extraction failed");
    } finally { setExtracting(false); }
  };

  const save = async () => {
    if (!extracted?.name) return toast.error("Formula needs a name");
    setSaving(true);
    try {
      const { data } = await api.post("/formulas", extracted);
      toast.success(`Saved '${data.name}'`);
      onSaved(data.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const setField = (k, v) => setExtracted((p) => ({ ...p, [k]: v }));
  const setIng = (i, k, v) => setExtracted((p) => {
    const ings = [...(p.ingredients || [])]; ings[i] = { ...ings[i], [k]: v };
    return { ...p, ingredients: ings };
  });
  const removeIng = (i) => setExtracted((p) => ({ ...p, ingredients: (p.ingredients || []).filter((_, idx) => idx !== i) }));
  const addIng = () => setExtracted((p) => ({ ...p, ingredients: [...(p.ingredients || []), { name: "", quantity: 0, unit: "mg" }] }));

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-pink-700" /> Import Master Formula from a legacy compounding record
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <Card className="p-3 border-pink-200 bg-pink-50/40">
            <p className="text-xs text-slate-700 mb-2">
              Upload a completed compounding record from LabFlo, PioneerRx, ScriptPro, or a scanned hand-written worksheet.
              Vera will use Gemini to reverse-engineer the underlying <b>Master Formula template</b> (recipe only — batch-specific data like lot #s and signatures are dropped).
              PDF, JPG, PNG, or plain text supported.
            </p>
            <div className="flex items-center gap-2">
              <input ref={inputRef} type="file" data-testid="import-file-input"
                accept=".pdf,.png,.jpg,.jpeg,.webp,.tif,.tiff,.txt,.csv"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                className="text-xs" />
              <Button data-testid="import-extract-btn" onClick={runExtract} disabled={!file || extracting}
                size="sm" className="h-8 gap-1 text-xs bg-pink-700 hover:bg-pink-800">
                {extracting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                Extract with AI
              </Button>
            </div>
          </Card>

          {extracted && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[10px] text-slate-500 uppercase">Formula name *</label>
                  <Input data-testid="import-name" value={extracted.name || ""}
                    onChange={(e) => setField("name", e.target.value)} className="h-9 text-sm" />
                </div>
                <div>
                  <label className="text-[10px] text-slate-500 uppercase">Dosage form</label>
                  <Input value={extracted.dosage_form || ""}
                    onChange={(e) => setField("dosage_form", e.target.value)} className="h-9 text-sm" />
                </div>
                <div>
                  <label className="text-[10px] text-slate-500 uppercase">Route</label>
                  <Input value={extracted.route_of_administration || ""}
                    onChange={(e) => setField("route_of_administration", e.target.value)} className="h-9 text-sm" />
                </div>
                <div>
                  <label className="text-[10px] text-slate-500 uppercase">BUD rule</label>
                  <Input value={extracted.bud_rule || ""}
                    onChange={(e) => setField("bud_rule", e.target.value)} className="h-9 text-sm" />
                </div>
                <div>
                  <label className="text-[10px] text-slate-500 uppercase">BUD days</label>
                  <Input type="number" value={extracted.bud_days || 0}
                    onChange={(e) => setField("bud_days", Number(e.target.value))} className="h-9 text-sm font-mono" />
                </div>
                <div>
                  <label className="text-[10px] text-slate-500 uppercase">Total qty</label>
                  <div className="flex gap-1">
                    <Input type="number" step="any" value={extracted.total_quantity || 0}
                      onChange={(e) => setField("total_quantity", Number(e.target.value))}
                      className="h-9 text-sm font-mono flex-1" />
                    <Input value={extracted.total_quantity_unit || "mL"}
                      onChange={(e) => setField("total_quantity_unit", e.target.value)}
                      className="h-9 text-sm w-16" />
                  </div>
                </div>
                <div className="col-span-2">
                  <label className="text-[10px] text-slate-500 uppercase">Storage conditions</label>
                  <Input value={extracted.storage_conditions || ""}
                    onChange={(e) => setField("storage_conditions", e.target.value)} className="h-9 text-sm" />
                </div>
              </div>

              <Card className="p-3 border-slate-200">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-sm font-semibold text-slate-800">Ingredients ({(extracted.ingredients || []).length})</div>
                  <Button size="sm" variant="outline" onClick={addIng} className="h-7 gap-1 text-xs">
                    <Plus className="w-3 h-3" /> Add
                  </Button>
                </div>
                <div className="space-y-1.5">
                  {(extracted.ingredients || []).map((ing, i) => (
                    <div key={i} className="flex gap-1.5" data-testid={`imp-ing-${i}`}>
                      <Input value={ing.name} onChange={(e) => setIng(i, "name", e.target.value)}
                        placeholder="Ingredient" className="h-8 text-xs flex-1" />
                      <Input type="number" step="any" value={ing.quantity || 0}
                        onChange={(e) => setIng(i, "quantity", Number(e.target.value))}
                        className="h-8 text-xs font-mono w-24" />
                      <Input value={ing.unit || "mg"} onChange={(e) => setIng(i, "unit", e.target.value)}
                        className="h-8 text-xs w-16" />
                      <button onClick={() => removeIng(i)} className="text-rose-500 hover:text-rose-700 px-2">
                        <ArchiveIcon className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              </Card>

              <div>
                <label className="text-[10px] text-slate-500 uppercase">Instructions (one per line)</label>
                <textarea data-testid="import-instructions" rows={4}
                  value={(extracted.instructions || []).join("\n")}
                  onChange={(e) => setField("instructions", e.target.value.split("\n").filter((s) => s.trim()))}
                  className="w-full text-xs px-2 py-1.5 rounded-md border border-slate-200 font-mono" />
              </div>
              <div>
                <label className="text-[10px] text-slate-500 uppercase">Equipment (one per line)</label>
                <textarea data-testid="import-equipment" rows={3}
                  value={(extracted.equipment || []).join("\n")}
                  onChange={(e) => setField("equipment", e.target.value.split("\n").filter((s) => s.trim()))}
                  className="w-full text-xs px-2 py-1.5 rounded-md border border-slate-200 font-mono" />
              </div>

              <Card className="p-3 border-slate-200">
                <div className="text-sm font-semibold text-slate-800 mb-2">
                  Vials, Stoppers &amp; Flip Caps ({(extracted.vial_specs || []).length})
                </div>
                {(extracted.vial_specs || []).length === 0 ? (
                  <p className="text-[11px] text-slate-400 italic">
                    AI didn't detect vial specs — add them on the formula detail page after saving.
                  </p>
                ) : (
                  <div className="space-y-1.5">
                    {(extracted.vial_specs || []).map((v, i) => (
                      <div key={i} className="flex gap-1.5" data-testid={`imp-vial-${i}`}>
                        <Input value={v.type || ""}
                          onChange={(e) => setField("vial_specs",
                            (extracted.vial_specs || []).map((x, idx) => idx === i ? { ...x, type: e.target.value } : x))}
                          placeholder="Type" className="h-8 text-xs flex-1" />
                        <Input value={v.size || ""}
                          onChange={(e) => setField("vial_specs",
                            (extracted.vial_specs || []).map((x, idx) => idx === i ? { ...x, size: e.target.value } : x))}
                          placeholder="Size" className="h-8 text-xs w-32" />
                      </div>
                    ))}
                  </div>
                )}
              </Card>

              <div>
                <label className="text-[10px] text-slate-500 uppercase">Disinfection agents (one per line)</label>
                <textarea data-testid="import-disinfection" rows={2}
                  value={(extracted.disinfection_agents || []).join("\n")}
                  onChange={(e) => setField("disinfection_agents", e.target.value.split("\n").filter((s) => s.trim()))}
                  className="w-full text-xs px-2 py-1.5 rounded-md border border-slate-200 font-mono" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[10px] text-slate-500 uppercase">CSP category</label>
                  <Input value={extracted.csp_category || ""}
                    onChange={(e) => setField("csp_category", e.target.value)} className="h-9 text-sm" />
                </div>
                <div className="flex gap-1">
                  <div className="flex-1">
                    <label className="text-[10px] text-slate-500 uppercase">pH min / max</label>
                    <div className="flex gap-1">
                      <Input type="number" step="any" value={(extracted.ph_target || {}).min || 0}
                        onChange={(e) => setField("ph_target", { ...(extracted.ph_target || {}), min: Number(e.target.value) || 0 })}
                        className="h-9 text-sm font-mono" placeholder="min" />
                      <Input type="number" step="any" value={(extracted.ph_target || {}).max || 0}
                        onChange={(e) => setField("ph_target", { ...(extracted.ph_target || {}), max: Number(e.target.value) || 0 })}
                        className="h-9 text-sm font-mono" placeholder="max" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button data-testid="import-save" onClick={save} disabled={!extracted || saving}
            className="bg-pink-700 hover:bg-pink-800 gap-1.5">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            Save as Master Formula
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
